// AI 답변에 실리는 차트 스펙 — ```chart 코드펜스 안의 JSON
//
// 모델이 코드를 실행하지 않고도 그래프를 낼 수 있게, 데이터와 차트 종류만 선언하게 한다.
// 렌더링은 전적으로 프론트가 맡는다 — 모델이 SVG나 이미지를 그리면 크기·색·접근성을
// 통제할 수 없고 답변마다 모양이 달라진다.

/** 시리즈 색 — 고정 순서로만 배정한다(순환·생성 금지).
 *  검증: 흰 배경(#ffffff) 기준 인접쌍 CVD ΔE 9.1, 일반시야 ΔE 19.6 통과. */
export const SERIES_COLORS = [
  '#2a78d6', // 1 blue
  '#eb6834', // 2 orange
  '#1baf7a', // 3 aqua
  '#eda100', // 4 yellow
  '#e87ba4', // 5 magenta
  '#008300', // 6 green
  '#4a3aa7', // 7 violet
  '#e34948', // 8 red
] as const;

/** 시리즈 상한 — 넘으면 색으로 구분이 안 된다. 표로 안내한다. */
export const MAX_SERIES = SERIES_COLORS.length;
/** 파이 조각 상한 — 넘으면 한눈에 비율이 안 읽힌다 */
export const MAX_PIE_SLICES = 6;
/** 파이 최소 조각 — 2조각짜리 파이는 그냥 숫자가 낫다 */
export const MIN_PIE_SLICES = 3;

export type ChartType = 'bar' | 'line' | 'pie';

export interface ChartPoint {
  label: string;
  value: number;
}

export interface ChartSeries {
  name: string;
  data: ChartPoint[];
}

export interface ChartSpec {
  type: ChartType;
  title?: string;
  /** 값 단위 — 축·툴팁·표에 붙는다 (예: "만원", "%") */
  unit?: string;
  /** 단일 시리즈 */
  data?: ChartPoint[];
  /** 다중 시리즈 (bar·line 전용) */
  series?: ChartSeries[];
}

/** 파싱·검증 결과 — 스펙이 깨졌으면 이유를 사용자에게 보여준다 */
export type ParsedChart =
  | { ok: true; spec: NormalizedChart }
  | { ok: false; reason: string };

/** 렌더러가 다루기 쉬운 형태로 정규화 — 단일/다중 시리즈를 한 모양으로 */
export interface NormalizedChart {
  type: ChartType;
  title?: string;
  unit?: string;
  /** 항목 이름 (x축 또는 파이 조각) */
  labels: string[];
  series: ChartSeries[];
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function toPoints(raw: unknown): ChartPoint[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const points: ChartPoint[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) return null;
    const { label, value } = item as Record<string, unknown>;
    if (typeof label !== 'string' || !isFiniteNumber(value)) return null;
    points.push({ label, value });
  }
  return points;
}

/**
 * 차트 스펙 파싱 — 깨진 JSON이나 빠진 필드는 조용히 넘기지 않는다.
 * 모델이 만든 값이라 형태가 어긋날 수 있고, 그때 빈 차트를 그리면 데이터가 없는 건지
 * 렌더링이 실패한 건지 사용자가 구분할 수 없다.
 */
export function parseChartSpec(source: string): ParsedChart {
  let raw: unknown;
  try {
    raw = JSON.parse(source);
  } catch {
    return { ok: false, reason: '차트 데이터를 읽지 못했습니다 (JSON 형식 오류)' };
  }
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, reason: '차트 데이터 형식이 올바르지 않습니다' };
  }

  const spec = raw as ChartSpec;
  const type = spec.type;
  if (type !== 'bar' && type !== 'line' && type !== 'pie') {
    return { ok: false, reason: `지원하지 않는 차트 종류입니다: ${String(type)}` };
  }

  let series: ChartSeries[];
  if (Array.isArray(spec.series) && spec.series.length > 0) {
    if (type === 'pie') {
      return { ok: false, reason: '파이 차트는 시리즈를 하나만 그릴 수 있습니다' };
    }
    const parsed: ChartSeries[] = [];
    for (const s of spec.series) {
      const points = toPoints(s?.data);
      if (!points || typeof s.name !== 'string') {
        return { ok: false, reason: '시리즈 데이터 형식이 올바르지 않습니다' };
      }
      parsed.push({ name: s.name, data: points });
    }
    series = parsed;
  } else {
    const points = toPoints(spec.data);
    if (!points) {
      return { ok: false, reason: '차트에 표시할 데이터가 없습니다' };
    }
    series = [{ name: spec.title ?? '', data: points }];
  }

  if (series.length > MAX_SERIES) {
    return { ok: false, reason: `시리즈가 ${MAX_SERIES}개를 넘으면 색으로 구분되지 않습니다 — 표로 보여주세요` };
  }

  // x축 항목은 첫 시리즈 기준 — 시리즈마다 항목이 다르면 축을 맞출 수 없다
  const labels = series[0].data.map(p => p.label);
  if (series.some(s => s.data.length !== labels.length)) {
    return { ok: false, reason: '시리즈마다 항목 수가 다릅니다' };
  }

  if (type === 'pie') {
    const n = labels.length;
    if (n < MIN_PIE_SLICES) {
      return { ok: false, reason: '조각이 2개 이하면 파이보다 숫자나 막대가 낫습니다' };
    }
    if (n > MAX_PIE_SLICES) {
      return { ok: false, reason: `파이는 ${MAX_PIE_SLICES}조각까지입니다 — 그 이상은 막대가 읽기 쉽습니다` };
    }
    if (series[0].data.some(p => p.value < 0)) {
      return { ok: false, reason: '파이 차트에는 음수를 넣을 수 없습니다' };
    }
  }

  return { ok: true, spec: { type, title: spec.title, unit: spec.unit, labels, series } };
}

/** 축 눈금·표에 쓰는 숫자 표기 — 천 단위 구분 */
export function formatValue(value: number, unit?: string): string {
  const n = Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return unit ? `${n}${unit}` : n;
}

/**
 * y축 눈금 — 0/1,000/2,000처럼 깔끔한 수로 끊는다.
 * 최댓값을 그대로 쓰면 1,237 같은 눈금이 생겨 읽는 데 부담이 된다.
 */
/**
 * 축 범위 결정 — 0에서 시작할지, 데이터 구간만 볼지.
 *
 * 막대는 **반드시 0에서 자란다**. 막대는 길이로 크기를 말하므로 축을 자르면 길이 비율이
 * 실제 값 비율과 어긋나 차이가 과장된다 (3,011과 3,014가 1:3으로 보이는 식).
 *
 * 꺾은선은 다르다. 읽는 사람이 보는 건 기울기와 변화이지 원점으로부터의 길이가 아니다.
 * 3,011~3,014처럼 값에 비해 변동이 아주 작을 때 0부터 그리면 직선으로 뭉개져 아무것도
 * 읽히지 않는다. 그래서 변동폭이 값 크기에 비해 작을 때만 축을 데이터 구간으로 좁힌다
 * (자를 때도 눈금 숫자가 3,010처럼 그대로 보이므로 0이 아님을 숨기지 않는다).
 */
export function computeScale(values: number[], type: ChartType): { ticks: number[]; bottom: number; top: number } {
  const dataMax = Math.max(...values);
  const dataMin = Math.min(...values);
  const spread = dataMax - dataMin;

  // 축을 좁히는 건 꺾은선뿐이고, 그중에서도 변동이 값 크기에 비해 작을 때만.
  // (음수가 섞이면 0이 구간 안에 있으므로 좁힐 이유가 없다)
  const zoom = type === 'line' && dataMin > 0 && spread > 0 && dataMin > spread * 2;

  if (!zoom) {
    const ticks = niceTicks(dataMax, Math.min(0, dataMin));
    return { ticks, bottom: ticks[0], top: ticks[ticks.length - 1] };
  }

  // 위아래로 10%씩 여백 — 선이 플롯 가장자리에 딱 붙지 않게
  const pad = spread * 0.1;
  const ticks = niceTicks(dataMax + pad, dataMin - pad, 4, false);
  return { ticks, bottom: ticks[0], top: ticks[ticks.length - 1] };
}

export function niceTicks(max: number, min: number, count = 4, includeZero = true): number[] {
  const lo = includeZero ? Math.min(0, min) : min;
  const span = max - lo || Math.max(Math.abs(max) * 0.1, 1); // 값이 전부 같을 때의 대비
  const rawStep = span / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;

  // 마지막 눈금은 반드시 최댓값 이상이어야 한다.
  // 눈금이 최댓값보다 낮게 끝나면 스케일이 그 눈금 기준으로 잡혀 값이 플롯 위로 삐져나간다
  // (실측: 최댓값 810에 눈금이 [0,500]에서 끊겨 y좌표가 -145까지 갔다).
  const ticks: number[] = [];
  const start = Math.floor(lo / step) * step;
  for (let v = start; ticks.length < 64; v += step) {
    ticks.push(Number(v.toFixed(10)));
    if (v >= max) break;
  }
  return ticks;
}
